import React, { useState, useEffect, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Input,
  FormGroup,
  Label,
  Alert,
  Spinner,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import MetaTags from "react-meta-tags";
import logo from "../../assets/images/logo/glogix-logo-white.png";

const DEFAULT_VENDORS = [
  { value: "osint_api", label: "Unified OSINT API (All-In-One)", tier: "Enterprise", desc: "Subdomains, DNS, Reverse IP/DNS, WHOIS, Leaks" },
  /*
  { value: "whoisxml_subdomains", label: "WhoisXML Subdomains API", tier: "Enterprise", desc: "Subdomain Enumeration" },
  { value: "ip2whois", label: "IP2Whois API", tier: "Pro", desc: "Domain WHOIS Registration" },
  { value: "dns_token", label: "DNS Audit API", tier: "Standard", desc: "DNS Record Resolution" },
  { value: "viewdns", label: "ViewDNS Info API", tier: "Standard", desc: "Portscan, Reverse IP, Reverse DNS" },
  { value: "dehashed", label: "DeHashed API", tier: "Enterprise", desc: "Credential Breach Intelligence" },
  { value: "leakosint", label: "LeakOSINT API", tier: "Enterprise", desc: "Credential & Breach Intelligence" },
  { value: "shodan", label: "Shodan Intelligence", tier: "Pro", desc: "Host & IoT Threat Intelligence" },
  { value: "virustotal", label: "VirusTotal API", tier: "Standard", desc: "Malware & IP Reputation" },
  { value: "securitytrails", label: "SecurityTrails API", tier: "Pro", desc: "Historical DNS & Subdomains" },
  */
];

const AdminDashboard = () => {
  const history = useHistory();
  const [keysList, setKeysList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Alert states
  const [alertInfo, setAlertInfo] = useState({ show: false, type: "", message: "" });
  const [testingVendor, setTestingVendor] = useState(null);
  const [cacheReloading, setCacheReloading] = useState(false);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCustomVendor, setIsCustomVendor] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [showRawKey, setShowRawKey] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    vendor: "",
    name: "",
    apiKey: "",
    authExtra: "",
    tier: "Standard",
    status: "active",
    description: "",
  });

  const adminToken = localStorage.getItem("adminToken");
  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");

  const getApiBase = () => {
    const defaultPath = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";
    return defaultPath.endsWith("/") ? defaultPath : `${defaultPath}/`;
  };

  const showAlert = (type, message, timeout = 5000) => {
    setAlertInfo({ show: true, type, message });
    if (timeout) {
      setTimeout(() => {
        setAlertInfo({ show: false, type: "", message: "" });
      }, timeout);
    }
  };

  // Fetch all API keys
  const fetchKeys = useCallback(async () => {
    if (!adminToken) {
      history.push("/admin-login");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${getApiBase()}api/admin/keys`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        history.push("/admin-login");
        return;
      }

      const data = await response.json();
      if (data.status === "success") {
        setKeysList(data.data || []);
      } else {
        showAlert("danger", data.message || "Failed to load keys.");
      }
    } catch (err) {
      console.error("Fetch keys error:", err);
      showAlert("danger", "Could not connect to API vault. Make sure the server is online.");
    } finally {
      setLoading(false);
    }
  }, [adminToken, history]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  // Open modal for Create
  const handleOpenAddModal = () => {
    setIsEditing(false);
    setIsCustomVendor(false);
    setShowRawKey(false);
    setFormData({
      vendor: "whoisxml_subdomains",
      name: "WhoisXML Subdomains API",
      apiKey: "",
      authExtra: "",
      tier: "Enterprise",
      status: "active",
      description: "Subdomain discovery & enumeration",
    });
    setModalOpen(true);
  };

  // Open modal for Edit
  const handleOpenEditModal = (item) => {
    setIsEditing(true);
    setIsCustomVendor(!DEFAULT_VENDORS.some((v) => v.value === item.vendor));
    setShowRawKey(false);
    setFormData({
      vendor: item.vendor,
      name: item.name || item.vendor,
      apiKey: "", // Leave blank unless updating
      authExtra: item.authExtra || "",
      tier: item.tier || "Standard",
      status: item.status || "active",
      description: item.description || "",
    });
    setModalOpen(true);
  };

  // Handle vendor preset selection. "Custom Vendor..." clears the vendor so the
  // slug can be typed in the custom field, which stays open while typing.
  const handleVendorSelectChange = (vendorVal) => {
    if (vendorVal === "custom_vendor") {
      setIsCustomVendor(true);
      setFormData((prev) => ({ ...prev, vendor: "", name: "", description: "" }));
      return;
    }
    setIsCustomVendor(false);
    const preset = DEFAULT_VENDORS.find((v) => v.value === vendorVal);
    if (preset) {
      setFormData((prev) => ({
        ...prev,
        vendor: preset.value,
        name: preset.label,
        tier: preset.tier,
        description: preset.desc,
      }));
    }
  };

  // Save API Key
  const handleSaveKey = async (e) => {
    e.preventDefault();
    if (!formData.vendor || (!formData.apiKey && !isEditing)) {
      showAlert("warning", "Please provide both vendor name and a valid API key.");
      return;
    }

    if (isEditing && !formData.apiKey) {
      showAlert("warning", "Please enter the new API Key to update.");
      return;
    }

    setSavingKey(true);
    try {
      const response = await fetch(`${getApiBase()}api/admin/keys`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (response.ok && data.status === "success") {
        showAlert("success", data.message || "Key saved and synced successfully!");
        setModalOpen(false);
        fetchKeys();
      } else {
        showAlert("danger", data.message || "Failed to save key.");
      }
    } catch (err) {
      console.error("Save key error:", err);
      showAlert("danger", "Failed to save key. Server error.");
    } finally {
      setSavingKey(false);
    }
  };

  // Delete API Key
  const handleDeleteKey = async (vendor) => {
    if (!window.confirm(`Are you sure you want to delete the API key for '${vendor}'? Services relying on it will stop working until updated.`)) {
      return;
    }

    try {
      const response = await fetch(`${getApiBase()}api/admin/keys/${vendor}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const data = await response.json();
      if (response.ok && data.status === "success") {
        showAlert("info", data.message || "Key removed from vault.");
        fetchKeys();
      } else {
        showAlert("danger", data.message || "Failed to delete key.");
      }
    } catch (err) {
      showAlert("danger", "Failed to delete key: " + err.message);
    }
  };

  // Test Vendor Key
  const handleTestKey = async (vendor) => {
    setTestingVendor(vendor);
    try {
      const response = await fetch(`${getApiBase()}api/admin/keys/test/${vendor}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const data = await response.json();
      if (data.status === "success") {
        showAlert("success", `[${vendor.toUpperCase()}] Verified: ${data.message}`);
      } else {
        showAlert("danger", `[${vendor.toUpperCase()}] Test failed: ${data.message}`);
      }
    } catch (err) {
      showAlert("danger", `Test error for ${vendor}: ${err.message}`);
    } finally {
      setTestingVendor(null);
    }
  };

  // Force Reload Cache
  const handleReloadCache = async () => {
    setCacheReloading(true);
    try {
      const response = await fetch(`${getApiBase()}api/admin/keys/refresh-cache`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });
      const data = await response.json();
      if (data.status === "success") {
        showAlert("success", "In-memory key cache reloaded from database!");
        fetchKeys();
      }
    } catch (err) {
      showAlert("danger", "Cache reload failed: " + err.message);
    } finally {
      setCacheReloading(false);
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    history.push("/login");
  };

  // Filtered keys
  const filteredKeys = keysList.filter((item) => {
    const matchesSearch =
      item.vendor?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeCount = keysList.filter((k) => k.status === "active").length;

  return (
    <React.Fragment>
      <MetaTags>
        <title>API Key Vault & Admin Console | Glogix</title>
      </MetaTags>

      <div
        className="min-vh-100 py-4"
        style={{
          backgroundColor: "#0d131f",
          color: "#e2e8f0",
          fontFamily: "inherit",
        }}
      >
        <Container fluid="lg">
          {/* Top Header Navbar */}
          <div
            className="d-flex flex-wrap align-items-center justify-content-between p-3 mb-4 rounded-3 shadow"
            style={{
              backgroundColor: "rgba(19, 26, 42, 0.9)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <img src={logo} alt="Glogix" height="34" />
              <div className="border-start border-secondary ps-3 ms-2">
                <h5 className="mb-0 text-white fw-bold d-flex align-items-center gap-2">
                  <FeatherIcon icon="shield" size={18} className="text-danger" />
                  API Key Management Vault
                </h5>
                <span className="text-muted fs-12">Dynamic Environment Configuration Center</span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
              <div className="d-none d-md-block me-2 text-end">
                <div className="text-white fs-13 fw-semibold">{adminUser.name || "Master Administrator"}</div>
                <div className="text-muted fs-11">{adminUser.email || "admin@guardlogix.com"}</div>
              </div>
              <Link to="/admin/clients" className="btn btn-outline-light btn-sm d-flex align-items-center gap-1" style={{ borderRadius: "6px" }}>
                <FeatherIcon icon="users" size={14} />
                <span className="d-none d-sm-inline">Clients</span>
              </Link>
              <Button
                color="outline-info"
                size="sm"
                onClick={handleReloadCache}
                disabled={cacheReloading}
                className="d-flex align-items-center gap-1"
                style={{ borderRadius: "6px" }}
              >
                {cacheReloading ? <Spinner size="sm" /> : <FeatherIcon icon="refresh-cw" size={14} />}
                <span className="d-none d-sm-inline">Purge Cache</span>
              </Button>
              <Button
                color="danger"
                size="sm"
                onClick={handleAdminLogout}
                className="d-flex align-items-center gap-1"
                style={{ borderRadius: "6px" }}
              >
                <FeatherIcon icon="log-out" size={14} />
                <span className="d-none d-sm-inline">Exit Portal</span>
              </Button>
            </div>
          </div>

          {/* Alert Notification */}
          {alertInfo.show && (
            <Alert
              color={alertInfo.type}
              className="d-flex align-items-center justify-content-between shadow-sm mb-4"
              style={{ borderRadius: "8px" }}
            >
              <div className="d-flex align-items-center">
                <FeatherIcon
                  icon={alertInfo.type === "success" ? "check-circle" : "alert-triangle"}
                  size={18}
                  className="me-2"
                />
                <span className="fs-13 fw-medium">{alertInfo.message}</span>
              </div>
              <Button
                close
                onClick={() => setAlertInfo({ show: false, type: "", message: "" })}
                className="p-0 fs-16"
              />
            </Alert>
          )}

          {/* Metric Overview Cards */}
          <Row className="g-3 mb-4">
            <Col sm={6} lg={3}>
              <Card
                className="border-0 shadow-sm h-100"
                style={{
                  backgroundColor: "#161f30",
                  borderLeft: "4px solid #3b82f6",
                  borderRadius: "10px",
                }}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-muted fs-12 fw-semibold text-uppercase">Total Configured</div>
                      <h3 className="mb-0 text-white fw-bold mt-1">{keysList.length}</h3>
                      <div className="text-info fs-11 mt-1">Vendor Endpoints</div>
                    </div>
                    <div
                      className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                      style={{ background: "rgba(59, 130, 246, 0.15)", color: "#3b82f6" }}
                    >
                      <FeatherIcon icon="database" size={22} />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col sm={6} lg={3}>
              <Card
                className="border-0 shadow-sm h-100"
                style={{
                  backgroundColor: "#161f30",
                  borderLeft: "4px solid #10b981",
                  borderRadius: "10px",
                }}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-muted fs-12 fw-semibold text-uppercase">Active Vault Keys</div>
                      <h3 className="mb-0 text-white fw-bold mt-1">{activeCount}</h3>
                      <div className="text-success fs-11 mt-1">100% Live In-Memory</div>
                    </div>
                    <div
                      className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                      style={{ background: "rgba(16, 185, 129, 0.15)", color: "#10b981" }}
                    >
                      <FeatherIcon icon="check-circle" size={22} />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col sm={6} lg={3}>
              <Card
                className="border-0 shadow-sm h-100"
                style={{
                  backgroundColor: "#161f30",
                  borderLeft: "4px solid #8b5cf6",
                  borderRadius: "10px",
                }}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-muted fs-12 fw-semibold text-uppercase">Encryption Tier</div>
                      <h3 className="mb-0 text-white fw-bold mt-1">AES-256</h3>
                      <div className="text-purple fs-11 mt-1" style={{ color: "#a78bfa" }}>
                        GCM Authenticated
                      </div>
                    </div>
                    <div
                      className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                      style={{ background: "rgba(139, 92, 246, 0.15)", color: "#8b5cf6" }}
                    >
                      <FeatherIcon icon="lock" size={22} />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col sm={6} lg={3}>
              <Card
                className="border-0 shadow-sm h-100"
                style={{
                  backgroundColor: "#161f30",
                  borderLeft: "4px solid #f59e0b",
                  borderRadius: "10px",
                }}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-muted fs-12 fw-semibold text-uppercase">Zero Downtime</div>
                      <h3 className="mb-0 text-white fw-bold mt-1">60s TTL</h3>
                      <div className="text-warning fs-11 mt-1">Auto Hot-Reload</div>
                    </div>
                    <div
                      className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                      style={{ background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}
                    >
                      <FeatherIcon icon="zap" size={22} />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Main Table Card */}
          <Card
            className="border-0 shadow-lg"
            style={{
              backgroundColor: "#161f30",
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            <div className="p-3 border-bottom border-secondary border-opacity-25 d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: "420px" }}>
                <div className="position-relative w-100">
                  <Input
                    type="text"
                    placeholder="Search by vendor, name, or service..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      backgroundColor: "rgba(11, 17, 32, 0.8)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                      borderRadius: "8px",
                      paddingLeft: "36px",
                    }}
                  />
                  <FeatherIcon
                    icon="search"
                    size={16}
                    className="position-absolute top-50 translate-middle-y text-muted"
                    style={{ left: "12px" }}
                  />
                </div>

                <Input
                  type="select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    backgroundColor: "rgba(11, 17, 32, 0.8)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    color: "#fff",
                    borderRadius: "8px",
                    width: "130px",
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="rate_limited">Rate Limited</option>
                </Input>
              </div>

              <div className="d-flex align-items-center gap-2">
                <Button
                  color="primary"
                  onClick={handleOpenAddModal}
                  className="d-flex align-items-center gap-1 shadow-sm"
                  style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                >
                  <FeatherIcon icon="plus" size={16} />
                  Add New API Key
                </Button>
              </div>
            </div>

            <CardBody className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="text-muted mt-2 fs-13">Decrypting and loading API keys from vault...</p>
                </div>
              ) : filteredKeys.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FeatherIcon icon="inbox" size={40} className="mb-2 opacity-50" />
                  <h5>No API keys found</h5>
                  <p className="fs-13">Click "Add New API Key" to register a dynamic vendor key.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle table-nowrap mb-0 table-hover table-dark">
                    <thead style={{ backgroundColor: "#111827", textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                      <tr>
                        <th className="ps-4">Vendor & Service</th>
                        <th>Masked API Key</th>
                        <th>Extra Auth</th>
                        <th>Tier</th>
                        <th>Status</th>
                        <th>Last Updated</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredKeys.map((item) => (
                        <tr key={item._id || item.vendor} style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}>
                          <td className="ps-4">
                            <div className="d-flex align-items-center">
                              <div
                                className="rounded p-2 me-3 d-flex align-items-center justify-content-center"
                                style={{
                                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                                  color: "#60a5fa",
                                  width: "36px",
                                  height: "36px",
                                }}
                              >
                                <FeatherIcon icon="key" size={16} />
                              </div>
                              <div>
                                <div className="fw-semibold text-white fs-14">{item.name || item.vendor}</div>
                                <div className="text-muted fs-12 font-monospace">{item.vendor}</div>
                              </div>
                            </div>
                          </td>

                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <code
                                className="px-2 py-1 rounded"
                                style={{
                                  backgroundColor: "rgba(0, 0, 0, 0.4)",
                                  color: "#34d399",
                                  border: "1px solid rgba(52, 211, 153, 0.2)",
                                  fontSize: "12px",
                                }}
                              >
                                {item.maskedKey || "••••••••••••"}
                              </code>
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-muted p-0"
                                title="Copy masked key"
                                onClick={() => {
                                  navigator.clipboard.writeText(item.maskedKey);
                                  showAlert("info", `Copied ${item.vendor} masked key!`, 2000);
                                }}
                              >
                                <FeatherIcon icon="copy" size={14} />
                              </button>
                            </div>
                          </td>

                          <td>
                            {item.authExtra ? (
                              <span className="badge bg-dark text-info border border-info border-opacity-25 font-monospace">
                                {item.authExtra}
                              </span>
                            ) : (
                              <span className="text-muted fs-12">—</span>
                            )}
                          </td>

                          <td>
                            <Badge
                              color={
                                item.tier === "Enterprise"
                                  ? "danger"
                                  : item.tier === "Pro"
                                  ? "warning"
                                  : "secondary"
                              }
                              className="text-uppercase"
                            >
                              {item.tier || "Standard"}
                            </Badge>
                          </td>

                          <td>
                            <Badge
                              color={
                                item.status === "active"
                                  ? "success"
                                  : item.status === "rate_limited"
                                  ? "warning"
                                  : "danger"
                              }
                              className="d-inline-flex align-items-center gap-1"
                              style={{ padding: "5px 10px", borderRadius: "12px" }}
                            >
                              <span
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  backgroundColor: item.status === "active" ? "#10b981" : "#ef4444",
                                }}
                              />
                              {item.status || "active"}
                            </Badge>
                          </td>

                          <td>
                            <div className="fs-12 text-light">
                              {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "Auto"}
                            </div>
                            <div className="fs-11 text-muted">by {item.updatedBy || "Admin"}</div>
                          </td>

                          <td className="text-end pe-4">
                            <div className="d-inline-flex align-items-center gap-1">
                              <Button
                                color="outline-light"
                                size="sm"
                                title="Test key connectivity"
                                disabled={testingVendor === item.vendor}
                                onClick={() => handleTestKey(item.vendor)}
                                style={{ borderRadius: "6px", padding: "4px 8px" }}
                              >
                                {testingVendor === item.vendor ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <FeatherIcon icon="activity" size={13} className="text-warning" />
                                )}
                              </Button>

                              <Button
                                color="outline-primary"
                                size="sm"
                                title="Edit / Update key"
                                onClick={() => handleOpenEditModal(item)}
                                style={{ borderRadius: "6px", padding: "4px 8px" }}
                              >
                                <FeatherIcon icon="edit-2" size={13} />
                              </Button>

                              <Button
                                color="outline-danger"
                                size="sm"
                                title="Delete from vault"
                                onClick={() => handleDeleteKey(item.vendor)}
                                style={{ borderRadius: "6px", padding: "4px 8px" }}
                              >
                                <FeatherIcon icon="trash-2" size={13} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Add / Edit Key Modal */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        centered
        style={{ color: "#fff" }}
        contentClassName="bg-dark border border-secondary border-opacity-50"
      >
        <ModalHeader
          toggle={() => setModalOpen(false)}
          className="border-bottom border-secondary border-opacity-25"
        >
          <div className="d-flex align-items-center gap-2">
            <FeatherIcon icon={isEditing ? "edit" : "plus-circle"} size={18} className="text-primary" />
            <span className="fw-bold">{isEditing ? `Update Key: ${formData.name}` : "Add New Dynamic Vendor Key"}</span>
          </div>
        </ModalHeader>

        <form onSubmit={handleSaveKey}>
          <ModalBody className="p-4">
            <FormGroup className="mb-3">
              <Label className="fs-13 fw-semibold text-light">Select Vendor Service</Label>
              <Input
                type="select"
                value={isCustomVendor ? "custom_vendor" : formData.vendor}
                disabled={isEditing}
                onChange={(e) => handleVendorSelectChange(e.target.value)}
                style={{
                  backgroundColor: "rgba(11, 17, 32, 0.9)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  color: "#fff",
                }}
              >
                {DEFAULT_VENDORS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label} ({v.value})
                  </option>
                ))}
                <option value="custom_vendor">Custom Vendor...</option>
              </Input>
            </FormGroup>

            {isCustomVendor && (
              <FormGroup className="mb-3">
                <Label className="fs-13 fw-semibold text-light">Custom Vendor Identifier (slug)</Label>
                <Input
                  type="text"
                  placeholder="e.g. hunter_io"
                  value={formData.vendor}
                  disabled={isEditing}
                  onChange={(e) => setFormData((prev) => ({ ...prev, vendor: e.target.value }))}
                  required
                  style={{
                    backgroundColor: "rgba(11, 17, 32, 0.9)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    color: "#fff",
                  }}
                />
              </FormGroup>
            )}

            <FormGroup className="mb-3">
              <Label className="fs-13 fw-semibold text-light">Display Name</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                required
                style={{
                  backgroundColor: "rgba(11, 17, 32, 0.9)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  color: "#fff",
                }}
              />
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="fs-13 fw-semibold text-light d-flex align-items-center justify-content-between">
                <span>{isEditing ? "New API Key (Paste to replace current key)" : "API Key / Token"}</span>
                <button
                  type="button"
                  className="btn btn-link btn-sm text-info p-0 text-decoration-none"
                  onClick={() => setShowRawKey(!showRawKey)}
                >
                  {showRawKey ? "Hide" : "Show"}
                </button>
              </Label>
              <div className="position-relative">
                <Input
                  type={showRawKey ? "text" : "password"}
                  placeholder={isEditing ? "Paste new replacement API key..." : "Enter vendor API key..."}
                  value={formData.apiKey}
                  onChange={(e) => setFormData((prev) => ({ ...prev, apiKey: e.target.value }))}
                  required={!isEditing}
                  style={{
                    backgroundColor: "rgba(11, 17, 32, 0.9)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    color: "#34d399",
                    fontFamily: "var(--font-mono)",
                  }}
                />
              </div>
              <small className="text-muted fs-11 mt-1 d-block">
                Will be encrypted using AES-256-GCM before saving into MongoDB.
              </small>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="fs-13 fw-semibold text-light">
                Extra Authentication (Optional Username / Email)
              </Label>
              <Input
                type="text"
                placeholder="e.g. admin@xiarch.com (Required for DeHashed)"
                value={formData.authExtra}
                onChange={(e) => setFormData((prev) => ({ ...prev, authExtra: e.target.value }))}
                style={{
                  backgroundColor: "rgba(11, 17, 32, 0.9)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  color: "#fff",
                }}
              />
            </FormGroup>

            <Row>
              <Col sm={6}>
                <FormGroup className="mb-3">
                  <Label className="fs-13 fw-semibold text-light">Tier / Plan</Label>
                  <Input
                    type="select"
                    value={formData.tier}
                    onChange={(e) => setFormData((prev) => ({ ...prev, tier: e.target.value }))}
                    style={{
                      backgroundColor: "rgba(11, 17, 32, 0.9)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                    }}
                  >
                    <option value="Enterprise">Enterprise</option>
                    <option value="Pro">Pro</option>
                    <option value="Standard">Standard</option>
                    <option value="Free">Free</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col sm={6}>
                <FormGroup className="mb-3">
                  <Label className="fs-13 fw-semibold text-light">Status</Label>
                  <Input
                    type="select"
                    value={formData.status}
                    onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                    style={{
                      backgroundColor: "rgba(11, 17, 32, 0.9)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="rate_limited">Rate Limited</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-0">
              <Label className="fs-13 fw-semibold text-light">Description / Notes</Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="Notes on usage or renewal date..."
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                style={{
                  backgroundColor: "rgba(11, 17, 32, 0.9)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  color: "#fff",
                }}
              />
            </FormGroup>
          </ModalBody>

          <ModalFooter className="border-top border-secondary border-opacity-25">
            <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={savingKey}
              className="d-flex align-items-center gap-1"
            >
              {savingKey ? <Spinner size="sm" /> : <FeatherIcon icon="save" size={15} />}
              Save & Sync Instantly
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </React.Fragment>
  );
};

export default AdminDashboard;
